import { CameraController } from './CameraController';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Loader } from './Loader';
import { GUI } from 'dat.gui';
import { CreatorAction, UUID } from '@/types/types';
import { ProductMeasures } from './ProductMeasures';
import { ActionHandler } from './ActionHandler';
import { Mouse } from './input/Mouse';
import { Keyboard } from './input/Keyboard';
import { EventEmitter } from './EventEmitter';
import { ThreeEvents } from './types';
import { Exporter, ExportType } from './Exporter';
import { SiphonsManager } from './SiphonsManager';
import { LightController } from './LightsController';
import {
	WebGLRenderer,
	Group,
	Scene,
	PCFShadowMap,
	SRGBColorSpace,
	Color,
	EquirectangularReflectionMapping,
	ACESFilmicToneMapping,
} from 'three';
import { ProductBuilder } from './ProductBuilder';
import { RenderUtils } from './RenderUtils';
import { Product } from '@/api/ProductApi';
import envMapPath from '@/assets/hdri/studio.exr';
import Stats from 'three/examples/jsm/libs/stats.module.js';

export type SyncAssetsPayload = Record<UUID, string>;

const stats = new Stats();
if (import.meta.env.DEV) document.body.appendChild(stats.dom);

export class ProjectRenderer {
	private static instance: ProjectRenderer = new ProjectRenderer();
	private labelRenderer!: CSS2DRenderer;
	private mouse = new Mouse(this);
	private keyboard = new Keyboard(this);
	private loader = Loader.getInstance();
	private exporter = new Exporter(this);
	private lightsController!: LightController;
	productMeasures = new ProductMeasures(this);
	productBuilder = new ProductBuilder(this);
	siphonsManager = new SiphonsManager(this);
	screenshotRenderer!: WebGLRenderer;
	actionHandler = new ActionHandler(this);
	renderer!: WebGLRenderer;
	emitter = new EventEmitter<ThreeEvents>();
	loading = false;
	GUI!: GUI;
	product = new Group();
	scene!: Scene;
	canvas: HTMLCanvasElement | null = null;
	cameraController!: CameraController;

	private constructor() {}

	static getInstance() {
		return ProjectRenderer.instance;
	}

	private initGUI() {
		this.GUI = new GUI();
	}

	private initRenderers() {
		this.renderer = new WebGLRenderer({
			canvas: this.canvas!,
			antialias: true,
		});

		const offscreenCanvas = new OffscreenCanvas(
			window.innerWidth,
			window.innerHeight
		);

		this.screenshotRenderer = new WebGLRenderer({
			canvas: offscreenCanvas,
			antialias: true,
			preserveDrawingBuffer: true,
		});

		for (const renderer of [this.renderer, this.screenshotRenderer]) {
			renderer.shadowMap.enabled = true;
			renderer.shadowMap.type = PCFShadowMap;
			renderer.outputColorSpace = SRGBColorSpace;
			renderer.toneMapping = ACESFilmicToneMapping;
		}

		this.labelRenderer = new CSS2DRenderer();

		this.labelRenderer.domElement.style.position = 'absolute';
		this.labelRenderer.domElement.style.zIndex = '9999';
		this.labelRenderer.domElement.style.top = '0px';
		this.labelRenderer.domElement.style.pointerEvents = 'none';
		this.canvas!.parentElement!.appendChild(this.labelRenderer.domElement);

		this.renderer.setAnimationLoop(() => {
			if (import.meta.env.DEV) stats.begin();

			this.cameraController.controls.update();

			this.renderer.render(this.scene, this.cameraController.camera);
			this.labelRenderer.render(this.scene, this.cameraController.camera);

			if (import.meta.env.DEV) stats.end();
		});
	}

	private loadEnvironmentMap() {
		this.loader.eXRLoader.load(envMapPath, (texture) => {
			texture.mapping = EquirectangularReflectionMapping;

			this.scene.environment = texture;
			this.scene.environmentIntensity = 0.4;
		});
	}

	toggleBoxMeasuresVisibility() {
		this.productMeasures.toggleVisibility();
	}

	init(canvas: HTMLCanvasElement) {
		this.canvas = canvas;

		if (import.meta.env.DEV) this.initGUI();

		this.scene = new Scene();

		this.mouse.init();
		this.keyboard.init();

		this.scene.background = new Color(RENDERER_CONFIG.SCENE_BACKGROUND);

		this.scene.add(this.product);

		this.cameraController = new CameraController(this);
		this.lightsController = new LightController(this);

		this.loadEnvironmentMap();
		this.initRenderers();

		this.handleResize();
	}

	handleActions(creatorActions: CreatorAction[], skipHistory?: true) {
		for (const action of creatorActions) {
			this.actionHandler.dispatchAction(action);
		}
	}

	screenshot() {
		this.cameraController.prepareScreenshotCamera();
		this.screenshotRenderer.render(
			this.scene,
			this.cameraController.screenshotCamera
		);

		return this.screenshotRenderer.domElement as unknown as OffscreenCanvas;
	}

	handleCameraCenter() {
		this.cameraController.centerCamera();
	}

	handleCameraZoom(cameraDistance: number) {
		this.cameraController.setDistanceToTarget(cameraDistance);
	}

	handleResize() {
		const { width, height } =
			this.canvas!.parentElement!.getBoundingClientRect();

		this.cameraController.handleResize(width / height);

		this.renderer.setSize(width, height);
		this.labelRenderer.setSize(width, height);
	}

	async startProduct(product: Product) {
		this.siphonsManager.clear();
		this.productBuilder.clear();
		RenderUtils.clearGroup(this.product);

		if (!product.model3d) throw new Error('MISSING_MODEL_3D');

		await this.productBuilder.loadProduct(product);
		this.cameraController.centerCamera();

		const actions = product.defaultVariation
			? product.defaultVariation?.attributeValues.reduce<CreatorAction[]>(
					(acc, curr) => {
						return acc.concat(curr.actions || []);
					},
					[]
				)
			: [];

		this.handleActions(actions, true);
	}

	handleExportCollection(type: ExportType) {
		this.exporter.exportCollection(type);
	}

	dispose() {
		this.renderer.dispose();
		this.cameraController.dispose();

		const ctx = this.canvas!.getContext('webgl2')!;
		ctx.clear(RENDERER_CONFIG.SCENE_BACKGROUND);
	}
}
